import { motion } from "framer-motion";
import React, { useState } from "react";
import FAQSection from "@/sections/FAQSection";
import CTASection from "@/sections/CTASection";
import FirmOperationsSnapshot from "@/sections/FirmOperationsSnapshot";

const tiers = [
    {
        name: "Starter",
        annualPrice: "$2,000",
        monthlyPrice: "$2,500",
        cadence: "/ month",
        billed: { annual: "Billed annually", monthly: "Billed monthly" },
        pitch: "Best for firms early in the AI adoption lifecycle or with basic requirements.",
        cta: "Go Starter",
        features: [
            "Onboarding & discovery",
            "1 core workflow",
            "Up to 2,000 minutes / month",
            "Standard integrations",
        ],
        highlighted: false,
    },
    {
        name: "Pro",
        annualPrice: "$5,000",
        monthlyPrice: "$6,250",
        cadence: "/ month",
        billed: { annual: "Billed annually", monthly: "Billed monthly" },
        pitch: "For firms with moderate volume — scaling intakes and optimizing workflows.",
        cta: "Go Pro",
        features: [
            "Everything in Starter",
            "Additional workflows",
            "Up to 6,000 minutes / month",
            "AI-driven data extraction into CMS",
            "Performance dashboard",
        ],
        highlighted: true,
    },
    {
        name: "Enterprise",
        annualPrice: "Talk to us",
        monthlyPrice: "Talk to us",
        cadence: "",
        billed: { annual: "Custom pricing", monthly: "Custom pricing" },
        pitch: "Best for firms with high volume and more complex demands.",
        cta: "Talk to us",
        features: [
            "Everything in Pro",
            "Unlimited workflows & agent deployments",
            "High-volume limits",
            "Custom integrations + semantic data layer",
            "Dedicated support + continuous optimization",
        ],
        highlighted: false,
    },
];

const comparison = [
    {
        section: "Intelligence & automation",
        rows: [
            { label: "Voice agent orchestration", values: ["✓", "✓", "✓"] },
            { label: "SMS & email workflows", values: ["✓", "✓", "✓"] },
            { label: "AI-driven case data extraction", values: ["—", "✓", "Cross-matter"] },
            { label: "Active conversation handling", values: ["Limited", "Full", "Full + adjuster ops"] },
            { label: "Multi-language routing (e.g. Spanish)", values: ["—", "✓", "✓"] },
        ],
    },
    {
        section: "Operations console",
        rows: [
            { label: "Live inbound dashboard", values: ["Basic", "Full", "Full + custom widgets"] },
            { label: "Per-queue agent assignments", values: ["1 queue", "Multi-queue", "Unlimited"] },
            { label: "Performance reports", values: ["Weekly digest", "Live dashboard", "Enterprise reporting"] },
            { label: "Call/transcript search", values: ["30 days", "1 year", "Unlimited"] },
        ],
    },
    {
        section: "Integrations & data",
        rows: [
            { label: "Standard telephony integrations", values: ["✓", "✓", "✓"] },
            { label: "Case management system sync", values: ["1 CMS", "Multi-CMS", "Multi-CMS + custom"] },
            { label: "Webhook automation", values: ["—", "✓", "✓"] },
            { label: "Custom integrations & semantic data layer", values: ["—", "—", "✓"] },
        ],
    },
    {
        section: "Support",
        rows: [
            { label: "Onboarding included", values: ["Standard", "Standard", "White-glove"] },
            { label: "Dedicated success manager", values: ["—", "Shared", "Dedicated"] },
            { label: "Continuous optimization", values: ["—", "Quarterly", "Continuous"] },
        ],
    },
];

const PricingPage = () => {
    const [billing, setBilling] = useState("annual");
    const isAnnual = billing === "annual";

    return (
        <main data-testid="pricing-page">
            {/* Hero */}
            <section className="relative pt-28 md:pt-36 pb-12 md:pb-16">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10 text-center">
                    <span className="eyebrow-pill">Pricing</span>
                    <h1 className="font-display text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.02] tracking-tight text-[var(--ink)] mt-5 font-semibold max-w-4xl mx-auto">
                        Built to pay for itself{" "}
                        <span className="accent-blue">in the first month.</span>
                    </h1>
                    <p className="mt-6 text-xl text-[var(--ink-soft)] leading-relaxed max-w-2xl mx-auto">
                        Make your team smarter without working harder. Every plan starts with a low-cost pilot to prove value before you scale.
                    </p>

                    {/* Billing toggle */}
                    <div className="mt-10 inline-flex items-center bg-[var(--paper)] border border-[var(--border-strong)] rounded-full p-1 relative">
                        <motion.div
                            aria-hidden
                            className="absolute top-1 bottom-1 rounded-full bg-[var(--blue-soft)] border border-[rgba(30,91,255,0.4)]"
                            animate={{ left: isAnnual ? 4 : "calc(50% + 0px)" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            style={{ width: "calc(50% - 4px)" }}
                        />
                        <button
                            type="button"
                            data-testid="billing-annual"
                            onClick={() => setBilling("annual")}
                            className="relative z-10 px-5 py-2.5 text-sm font-medium flex items-center gap-2 min-w-[160px] justify-center"
                            style={{ color: isAnnual ? "var(--blue-deep)" : "var(--ink-dim)" }}
                        >
                            Annual
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--blue)] text-white tracking-wider">
                                −20%
                            </span>
                        </button>
                        <button
                            type="button"
                            data-testid="billing-monthly"
                            onClick={() => setBilling("monthly")}
                            className="relative z-10 px-5 py-2.5 text-sm font-medium min-w-[160px] justify-center"
                            style={{ color: !isAnnual ? "var(--blue-deep)" : "var(--ink-dim)" }}
                        >
                            Monthly
                        </button>
                    </div>
                </div>
            </section>

            {/* Tier cards */}
            <section className="relative py-12 md:py-16">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {tiers.map((tier, i) => (
                        <TierCard key={tier.name} tier={tier} isAnnual={isAnnual} index={i} />
                    ))}
                </div>
            </section>

            {/* Comparison table */}
            <section className="relative py-16 md:py-24 bg-[var(--cream-2)] border-y border-[var(--border)]">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="mb-10 max-w-3xl"
                    >
                        <span className="eyebrow-pill">Compare plans</span>
                        <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            Side by side, in detail.
                        </h2>
                    </motion.div>

                    <div className="card-paper overflow-x-auto">
                        <table className="w-full text-left min-w-[720px]" data-testid="pricing-compare-table">
                            <thead>
                                <tr className="border-b border-[var(--border)]">
                                    <th className="p-5 font-display text-[var(--ink-dim)] font-mono text-[10px] tracking-[0.2em] uppercase w-[40%]">
                                        Feature
                                    </th>
                                    {tiers.map((t) => (
                                        <th key={t.name} className="p-5 font-display text-[var(--ink)] text-base font-semibold w-[20%]">
                                            {t.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {comparison.map((sec) => (
                                    <React.Fragment key={sec.section}>
                                        <tr className="bg-[var(--cream-2)]/40 border-y border-[var(--border)]">
                                            <td colSpan={4} className="px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--blue-deep)] font-semibold">
                                                {sec.section}
                                            </td>
                                        </tr>
                                        {sec.rows.map((r, i) => (
                                            <tr key={`${sec.section}-${i}`} className="border-b border-[var(--border)] last:border-b-0">
                                                <td className="px-5 py-3.5 text-sm text-[var(--ink)]">{r.label}</td>
                                                {r.values.map((v, j) => (
                                                    <td key={`${r.label}-${j}`} className={`px-5 py-3.5 text-sm ${v === "—" ? "text-[var(--ink-dim)]" : "text-[var(--ink)]"}`}>
                                                        {v === "✓" ? <i className="fa-solid fa-check accent-blue" /> : v}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <FirmOperationsSnapshot />

            <FAQSection />
            <CTASection />
        </main>
    );
};

const TierCard = ({ tier, isAnnual, index }) => {
    const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
    const isHighlighted = tier.highlighted;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: index * 0.08 }}
            whileHover={{ y: -4 }}
            data-testid={`tier-${tier.name.toLowerCase()}`}
            className={`relative rounded-2xl p-7 md:p-8 flex flex-col ${
                isHighlighted
                    ? "bg-[var(--ink)] text-[var(--cream)] border border-[var(--ink)]"
                    : "card-paper"
            }`}
        >
            {isHighlighted && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[var(--blue)] text-white text-[10px] font-mono tracking-[0.18em] uppercase font-semibold">
                    Most popular
                </span>
            )}
            <div className={`font-mono text-[11px] tracking-[0.2em] uppercase font-semibold mb-3 ${isHighlighted ? "text-[var(--cream)]/70" : "text-[var(--muted)]"}`}>
                {tier.name}
            </div>
            <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-[clamp(2.4rem,4vw,3.2rem)] font-semibold tabular-nums leading-none">{price}</span>
                {tier.cadence && (
                    <span className={`text-sm ${isHighlighted ? "text-[var(--cream)]/65" : "text-[var(--ink-soft)]"}`}>{tier.cadence}</span>
                )}
            </div>
            <p className={`text-xs font-mono tracking-wider uppercase mb-5 ${isHighlighted ? "text-[var(--cream)]/60" : "text-[var(--muted)]"}`}>
                {isAnnual ? tier.billed.annual : tier.billed.monthly}
            </p>
            <p className={`text-[15px] leading-relaxed mb-6 ${isHighlighted ? "text-[var(--cream)]/85" : "text-[var(--ink-soft)]"}`}>
                {tier.pitch}
            </p>
            <ul className="space-y-2.5 mb-7 flex-1">
                {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[14px]">
                        <i className={`fa-solid fa-check text-xs mt-1.5 flex-shrink-0 ${isHighlighted ? "text-[#6EA5FF]" : "accent-blue"}`} />
                        <span className={isHighlighted ? "text-[var(--cream)]" : "text-[var(--ink)]"}>{f}</span>
                    </li>
                ))}
            </ul>
            <a
                href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                target="_blank"
                rel="noreferrer"
                data-testid={`tier-cta-${tier.name.toLowerCase()}`}
                className={`mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all ${
                    isHighlighted
                        ? "bg-[var(--cream)] text-[var(--ink)] hover:bg-white"
                        : "bg-[var(--ink)] text-white hover:bg-black"
                }`}
            >
                {tier.cta}
                <i className="fa-solid fa-arrow-right text-[10px]" />
            </a>
        </motion.div>
    );
};

export default PricingPage;
